import React, { MouseEventHandler, useRef } from "react";
import { useState, useEffect } from "react";
import { Box, Button, TextField, Typography, Container, Grid, Paper, MenuItem, Select, FormControl, InputLabel, SelectChangeEvent, Dialog, DialogTitle, DialogContent, IconButton, Divider, Chip, CircularProgress, Alert, Stack, List, ListItem, ListItemText, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import QRCodeScanner from "./QRcodeScanner";
import { useAuth } from '../contexts/AuthContext';
import { getUserDetails, getProtectedResourceDetail, purchasePoints, getLiveEvents, getPointTransactions, getVisitLogs, deletePointTransaction, deleteVisitLog } from "../services/api";
import { useNavigate } from "react-router-dom";
import { format } from 'date-fns';

interface userinfo {
  name: string;
  email: string;
  id: string;
  rank: string;
  points: number;
  times: number;
  is_staff: boolean;
}

interface LiveEvent {
  id: number;
  title: string;
  start_at: string;
  end_at: string | null;
  description: string;
  location: string;
  points_award: number;
  publish_at: string | null;
}

interface PointTransaction {
  id: number;
  user: number;
  transaction_type: string;
  amount: number;
  live_event_title: string | null;
  created_at: string;
}

interface VisitLog {
  id: number;
  user: number;
  live_event_title: string;
  points_awarded: number;
  created_at: string;
}

const PointRegister: React.FC = () => {
  const { token, refreshTokenFunction, logout, isStaff, setIsStaff } = useAuth();
  const [scannedText, setScannedText] = useState<string | null>(null);
  const [userID, setUserID] = useState<string | null>(null);
  const [inputUserID, setInputUserID] = useState<string>("");
  const [message, setMessage] = useState<userinfo>();
  const [readable, setReadble] = useState<boolean>(true);
  const [additonalPoints, setAdditionalPoints] = useState<number>(0);
  const [liveEvents, setLiveEvents] = useState<LiveEvent[]>([]);
  const [selectedLiveEvent, setSelectedLiveEvent] = useState<number | ''>('');
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string>('');
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [visitLogs, setVisitLogs] = useState<VisitLog[]>([]);

  const navigate = useNavigate();

  // ライブイベント一覧を取得
  useEffect(() => {
    const fetchLiveEvents = async () => {
      if (token) {
        try {
          const response = await getLiveEvents(token);
          const events = response.data;
          setLiveEvents(events);
          
          // 本日開催のライブイベントを自動選択
          const today = new Date();
          const todayStr = format(today, 'yyyy-MM-dd');
          const todayEvent = events.find((event: LiveEvent) => {
            const eventDate = format(new Date(event.start_at), 'yyyy-MM-dd');
            return eventDate === todayStr;
          });
          
          if (todayEvent) {
            setSelectedLiveEvent(todayEvent.id);
          }
        } catch (error) {
          console.error('ライブイベントの取得に失敗:', error);
        }
      }
    };
    fetchLiveEvents();
  }, [token]);

  useEffect(() => {
    const fetchProtectedResource = async () => {
      if (token) {
        try {
          const response = await getProtectedResourceDetail(token);
          setIsStaff(response.data.is_staff);
        } catch (error: any) {
          if (error.response && error.response.status === 401) {
            try {
              const newToken = await refreshTokenFunction();
              if (newToken) {
                const retryResponse = await getProtectedResourceDetail(newToken);
                setIsStaff(retryResponse.data.is_staff);
                return;
              }
            } catch (refreshError) { }
          }
          alert('エラーが発生しました。再度ログインしてください。');
          logout();
        }
      }
    };
    fetchProtectedResource();
  }, [token]);

  useEffect(() => {
    const fetchProtectedResource = async () => {
      if (token && userID) {
        try {
          const response = await getUserDetails(userID, token);
          console.log(response);
          setMessage(response.data);
        } catch (error: any) {
          console.error('Failed to fetch protected resource:', error);
        }
      }
    };
    fetchProtectedResource();
  }, [token, userID]);

  const postUserID: MouseEventHandler = () => {
    setUserID(inputUserID);
  };

  const updatePointHandler: MouseEventHandler = async () => {
    if (token && userID && message) {
      console.log('message全体:', message);
      console.log('message.id:', message.id, 'typeof:', typeof message.id);
      
      const numericUserId = Number(message.id);
      console.log('numericUserId:', numericUserId, 'isNaN:', isNaN(numericUserId));
      
      if (isNaN(numericUserId)) {
        alert('ユーザーIDが不正です');
        return;
      }
      
      try {
        console.log('ポイント付与開始:', {
          userId: numericUserId,
          amount: additonalPoints,
          liveEventId: selectedLiveEvent !== '' ? Number(selectedLiveEvent) : undefined
        });
        
        // 新しいAPI (purchasePoints) を使用してポイント付与
        const result = await purchasePoints(
          token, 
          numericUserId, 
          additonalPoints, 
          selectedLiveEvent !== '' ? Number(selectedLiveEvent) : undefined,
          ''
        );
        
        console.log('ポイント付与成功:', result.data);
        
        // ユーザー情報を再取得
        const response = await getUserDetails(userID, token);
        alert("付与が完了しました")
        setMessage(response.data);
        setAdditionalPoints(0);
      } catch (error: any) {
        console.error('ポイント付与エラー:', error);
        console.error('エラーレスポンス:', error.response?.data);
        alert(`ポイント付与に失敗しました: ${error.response?.data?.error || error.message}`);
      }
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'purchase': return '付与';
      case 'visit': return '来店';
      case 'redeem': return '交換';
      case 'adjust': return '調整';
      default: return type;
    }
  };

  const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (type) {
      case 'purchase': return 'info';
      case 'visit': return 'success';
      case 'redeem': return 'warning';
      case 'adjust': return 'secondary';
      default: return 'default';
    }
  };

  const fetchHistories = async () => {
    if (!token || !message?.id) return;

    const targetId = Number(message.id);
    if (isNaN(targetId)) return;

    setHistoryLoading(true);
    setHistoryError('');

    const runFetch = async (useToken: string) => {
      const [txRes, visitRes] = await Promise.all([
        getPointTransactions(useToken),
        getVisitLogs(useToken)
      ]);

      setTransactions(txRes.data.filter((tx: PointTransaction) => tx.user === targetId));
      setVisitLogs(visitRes.data.filter((visit: VisitLog) => visit.user === targetId));
    };

    try {
      await runFetch(token);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        try {
          const newToken = await refreshTokenFunction();
          if (newToken) {
            await runFetch(newToken);
            return;
          }
        } catch (refreshError) { }
      }
      setHistoryError('履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = async () => {
    setHistoryOpen(true);
    await fetchHistories();
  };

  const handleDeleteTransaction = async (id: number) => {
    if (!token) return;
    if (!window.confirm('このポイント履歴を削除しますか？')) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await deletePointTransaction(token, id);
      if (res?.data?.points !== undefined) {
        setMessage((prev) => prev ? { ...prev, points: res.data.points, times: res.data.times ?? prev.times } : prev);
      }
      await fetchHistories();
    } catch (err: any) {
      setHistoryError('ポイント履歴の削除に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDeleteVisitLog = async (id: number) => {
    if (!token) return;
    if (!window.confirm('この参加履歴を削除しますか？')) return;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res = await deleteVisitLog(token, id);
      if (res?.data?.times !== undefined) {
        setMessage((prev) => prev ? { ...prev, points: res.data.points ?? prev.points, times: res.data.times } : prev);
      }
      await fetchHistories();
    } catch (err: any) {
      setHistoryError('参加履歴の削除に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    setReadble(false);
    setUserID(scannedText);
  }, [scannedText]);

  if (isStaff) {
    return (
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ padding: 4, marginTop: 7 }}>
          <Box mb={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>登録手順</Typography>
            <Box component="ol" sx={{ pl: 3, mb: 0 }}>
              <Typography component="li" variant="body2">QRを読み取るかメールアドレスを入力して「更新」で対象ユーザーを取得。</Typography>
              <Typography component="li" variant="body2">名前・ポイント・来場回数が表示されていることを確認。</Typography>
              <Typography component="li" variant="body2">付与するポイント数を入力し、必要に応じてライブイベントを選択。</Typography>
              <Typography component="li" variant="body2">「付与」を押して完了メッセージを確認。</Typography>
              <Typography component="li" variant="body2">別ユーザーを続けて登録する場合は再度QR読み込みまたはメールアドレス入力を実施。</Typography>
            </Box>
          </Box>
          {(readable) ? (
            <QRCodeScanner secretKey="moka" scannedText={scannedText} setScannedText={setScannedText} />
          ) : (
            <Box display={'flex'} justifyContent={'center'}>
              <Button variant="contained" onClick={() => setReadble(true)} sx={{ width: '50vw', height: '25vw' }}>QR読み込み開始</Button>
            </Box>
          )}
          <Box mt={2}>
            <TextField
              fullWidth
              label="メールアドレス"
              variant="outlined"
              onChange={(event) => setInputUserID(event.target.value)}
            />
            <Button fullWidth variant="contained" color="primary" onClick={postUserID} sx={{ marginTop: 2 }}>更新</Button>
          </Box>
          <Box mt={2}>
            <Typography variant="h6">{message?.name} 様</Typography>
            <Typography variant="body1">ポイント: {message?.points}</Typography>
            <Typography variant="body1">来場回数: {message?.times}</Typography>
            <Box mt={1}>
              <Button variant="outlined" size="small" disabled={!message?.id} onClick={handleOpenHistory}>履歴を見る</Button>
            </Box>
          </Box>
          <Box mt={2}>
            <TextField
              fullWidth
              type="number"
              label="ポイント"
              variant="outlined"
              value={additonalPoints}
              onChange={(event) => setAdditionalPoints(Number(event.target.value))}
            />
          </Box>
          <Box mt={2}>
            <FormControl fullWidth>
              <InputLabel id="live-event-select-label">ライブイベント</InputLabel>
              <Select
                labelId="live-event-select-label"
                value={selectedLiveEvent}
                label="ライブイベント"
                onChange={(event: SelectChangeEvent<number | ''>) => {
                  setSelectedLiveEvent(event.target.value as number | '');
                }}
              >
                <MenuItem value="">なし</MenuItem>
                {liveEvents.map((event) => (
                  <MenuItem key={event.id} value={event.id}>
                    {event.title} ({format(new Date(event.start_at), 'yyyy/MM/dd HH:mm')})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button fullWidth variant="contained" color="secondary" onClick={updatePointHandler} sx={{ marginTop: 2 }}>付与</Button>
          </Box>
          <Box display={'flex'} justifyContent={'center'}>
            <Button variant="outlined" color="error" onClick={() => { logout(); navigate('/'); }} sx={{ marginTop: 10 }}>Logout</Button>
          </Box>
          <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {message?.name} さんの履歴
              <IconButton aria-label="close" onClick={() => setHistoryOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {historyLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={3}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <Stack spacing={3}>
                  {historyError && <Alert severity="error">{historyError}</Alert>}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>ポイント履歴</Typography>
                    {transactions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">履歴がありません</Typography>
                    ) : (
                      <List dense>
                        {transactions.slice(0, 20).map((tx) => (
                          <React.Fragment key={tx.id}>
                            <ListItem alignItems="flex-start" sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={
                                  <Chip size="small" label={getTypeLabel(tx.transaction_type)} color={getTypeColor(tx.transaction_type)} />
                                }
                                secondary={
                                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                                    <Typography variant="caption" color="text.secondary">{format(new Date(tx.created_at), 'yyyy/MM/dd HH:mm')}</Typography>
                                    <Typography variant="subtitle2" color={tx.amount >= 0 ? 'success.main' : 'error.main'} fontWeight="bold">{tx.amount >= 0 ? '+' : ''}{tx.amount}</Typography>
                                  </Box>
                                }
                              />
                              <Tooltip title="削除">
                                <IconButton edge="end" size="small" onClick={() => handleDeleteTransaction(tx.id)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItem>
                            <Divider component="li" />
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>参加イベント履歴</Typography>
                    {visitLogs.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">履歴がありません</Typography>
                    ) : (
                      <List dense>
                        {visitLogs.slice(0, 20).map((visit) => (
                          <React.Fragment key={visit.id}>
                            <ListItem sx={{ py: 0.5 }}>
                              <ListItemText
                                primary={visit.live_event_title}
                                secondary={
                                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                                    <Typography variant="caption" color="text.secondary">{format(new Date(visit.created_at), 'yyyy/MM/dd HH:mm')}</Typography>
                                    <Typography variant="caption" color="text.secondary">付与: {visit.points_awarded}</Typography>
                                  </Box>
                                }
                              />
                              <Tooltip title="削除">
                                <IconButton edge="end" size="small" onClick={() => handleDeleteVisitLog(visit.id)}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItem>
                            <Divider component="li" />
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Box>
                </Stack>
              )}
            </DialogContent>
          </Dialog>
        </Paper>
      </Container>
    );
  } else {
    return <></>;
  }
};

export default PointRegister;
